import { useState } from 'react';
import { Save, Bell, Download, Trash2, Key, Copy, Check, AlertTriangle } from 'lucide-react';
import { Toggle, Spinner, Modal } from '@/components/shared/UIComponents';
import { usePlatformStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import type { Tenant } from '@/types';

export function SettingsPage({ tenant }: { tenant: Tenant }) {
  const { updateTenant, suspendTenant } = usePlatformStore();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const [form, setForm] = useState({
    name: tenant.name,
    slug: tenant.slug,
    owner_email: user?.email || '',
  });

  const defaultNotifs = { usage_alerts: true, billing_alerts: true, user_signups: true, weekly_digest: false, notification_retention_days: 30 };
  const [notifications, setNotifications] = useState(
    { ...defaultNotifs, ...tenant.notification_prefs }
  );
  const [retentionInput, setRetentionInput] = useState(
    String(tenant.notification_prefs?.notification_retention_days ?? 30)
  );

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateTenant(tenant.id, { name: form.name, notification_prefs: notifications } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {} finally { setSaving(false); }
  };

  const handleSuspend = async () => {
    setSuspending(true);
    try {
      await suspendTenant(tenant.id);
      setShowSuspendModal(false);
    } catch {} finally {
      setSuspending(false);
    }
  };

  const handleCopyTenantId = () => {
    navigator.clipboard.writeText(tenant.id);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      // Build a JSON export of the tenant data
      const exportData = {
        tenant: {
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          subdomain: tenant.subdomain,
          status: tenant.status,
          created_at: tenant.created_at,
        },
        branding: tenant.branding || {},
        features: tenant.features || {},
        plan: tenant.plan || {},
        exported_at: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenant.slug}-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {} finally {
      setExportingData(false);
    }
  };

  const isSuspended = tenant.status === 'cancelled';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Settings</h1>
          <p className="text-sm text-surface-500 mt-1">Organisation settings and preferences</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-400">Saved!</span>}
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? <Spinner size="sm" /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Suspended banner */}
      {isSuspended && (
        <div className="p-4 rounded-xl banner-red border">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-400" />
            <div>
              <div className="text-sm font-medium text-red-800">This tenant is suspended</div>
              <div className="text-xs text-red-400 mt-0.5">Users cannot access the application. Contact support to reactivate.</div>
            </div>
          </div>
        </div>
      )}

      {/* Organisation details */}
      <div className="glass-panel p-5">
        <h3 className="text-sm font-semibold text-surface-900 mb-4">Organisation Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="input-label">Organisation Name</label>
            <input className="input-field" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label className="input-label">URL Slug</label>
            <div className="relative">
              <input className="input-field pr-28 bg-surface-50" value={form.slug} disabled />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-surface-500">.discoverygem.com</span>
            </div>
            <p className="text-xs text-surface-500 mt-1">Slug cannot be changed after creation</p>
          </div>
          <div>
            <label className="input-label">Tenant ID</label>
            <input className="input-field bg-surface-50 font-mono text-xs" value={tenant.id} disabled />
          </div>
          <div>
            <label className="input-label">Created</label>
            <input className="input-field bg-surface-50" value={new Date(tenant.created_at).toLocaleDateString()} disabled />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={16} className="text-brand-600" />
          <h3 className="text-sm font-semibold text-surface-900">Notifications</h3>
        </div>
        <p className="text-xs text-surface-500 mb-3">Configure which email notifications are sent to your admin team.</p>
        <div className="space-y-1">
          <Toggle enabled={notifications.usage_alerts} onChange={v => setNotifications(n => ({ ...n, usage_alerts: v }))}
            label="Usage alerts" description="Get notified when approaching plan limits" />
          <Toggle enabled={notifications.billing_alerts} onChange={v => setNotifications(n => ({ ...n, billing_alerts: v }))}
            label="Billing alerts" description="Payment failures and subscription changes" />
          <Toggle enabled={notifications.user_signups} onChange={v => setNotifications(n => ({ ...n, user_signups: v }))}
            label="New user signups" description="When a new user joins the organisation" />
          <Toggle enabled={notifications.weekly_digest} onChange={v => setNotifications(n => ({ ...n, weekly_digest: v }))}
            label="Weekly digest" description="Summary of activity and usage every Monday" />
        </div>

        <div className="mt-5 pt-5 border-t border-surface-200">
          <h4 className="text-sm font-medium text-surface-900 mb-1">Notification Retention</h4>
          <p className="text-xs text-surface-500 mb-3">
            How many days to keep user notifications under All Activities. Older notifications are automatically removed.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={365}
              value={retentionInput}
              onChange={e => {
                setRetentionInput(e.target.value);
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1) {
                  setNotifications(n => ({ ...n, notification_retention_days: val }));
                }
              }}
              className="input-field w-24"
              placeholder="30"
            />
            <span className="text-sm text-surface-500">days</span>
          </div>
        </div>
      </div>

      {/* API & Data */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Key size={16} className="text-brand-600" />
          <h3 className="text-sm font-semibold text-surface-900">API & Data</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="text-sm font-medium text-surface-900 mb-1">Tenant ID</div>
            <div className="text-xs text-surface-500 mb-3">Use this ID to reference your tenant in API calls</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-surface-200 p-2 rounded font-mono truncate">{tenant.id}</code>
              <button onClick={handleCopyTenantId} className="btn-secondary text-xs p-2">
                {copiedKey ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
              </button>
            </div>
          </div>
          <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="text-sm font-medium text-surface-900 mb-1">Export Data</div>
            <div className="text-xs text-surface-500 mb-3">Download tenant configuration as JSON</div>
            <button onClick={handleExportData} disabled={exportingData} className="btn-secondary text-xs">
              {exportingData ? <Spinner size="sm" /> : <Download size={12} />}
              {exportingData ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      {!isSuspended && (
        <div className="glass-panel p-5 border-red-800/40">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 size={16} className="text-red-500" />
            <h3 className="text-sm font-semibold text-red-400">Danger Zone</h3>
          </div>
          <div className="flex items-center justify-between p-4 banner-red border rounded-xl">
            <div>
              <div className="text-sm font-medium text-red-800">Suspend Tenant</div>
              <div className="text-xs text-red-400 mt-0.5">Temporarily disable this tenant. Users will not be able to access the app.</div>
            </div>
            <button onClick={() => setShowSuspendModal(true)} className="btn-danger text-sm">Suspend</button>
          </div>
        </div>
      )}

      {/* Suspend confirmation modal */}
      <Modal open={showSuspendModal} onClose={() => setShowSuspendModal(false)}
        title="Suspend Tenant" subtitle="This will immediately disable access for all users">
        <div className="space-y-4">
          <div className="p-4 banner-red border rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium">Are you sure you want to suspend "{tenant.name}"?</p>
                <p className="text-xs text-red-400 mt-1">All users will lose access immediately. This action can be reversed by contacting support.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowSuspendModal(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleSuspend} disabled={suspending} className="btn-danger text-sm">
              {suspending ? <Spinner size="sm" /> : <Trash2 size={14} />}
              {suspending ? 'Suspending...' : 'Confirm Suspend'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
